import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RefreshCw, Info } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ReplenishmentRowView,
  ReplenishThresholdType,
  getReplenishmentSettings,
  updateReplenishmentThreshold,
} from "../../../../api/General/assets";
import { getCompanyRules, updateCompanyRules } from "../../../../api/General/Company";
import type { CompanyRulesInput } from "../../../../models/general";
import type { RootState } from "../../../../store/redux/store/store";
import { Spinner } from "@/components/ui/Spinner";
import { Switch } from "@/components/ui/Switch";
import StickyActionBar from "@/components/ui/StickyActionBar";
import { useToast, ToastRenderer } from "@/hooks/useToast";

interface RowEdit {
  threshold: string;
  thresholdType: ReplenishThresholdType;
}

function toEdit(row: ReplenishmentRowView): RowEdit {
  return {
    threshold: row.threshold != null ? String(row.threshold) : "",
    thresholdType: row.thresholdType ?? ReplenishThresholdType.Amount,
  };
}

export default function ReplenishmentTab() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const companyId = useSelector((state: RootState) => state.user.company?.id);
  // CO-354 — writing the toggle goes through Company/UpdateCompanyRules, which is Policy="Hr".
  // Asset managers who are not HR reach this tab (useCanManageAssets = tag || Hr) and may still
  // edit thresholds, so the toggle is shown read-only for them rather than handing them a 403.
  const role = useSelector((state: RootState) => state.user.role);
  const canEditNotificationToggle = role === "Hr";

  const [rows, setRows] = useState<ReplenishmentRowView[]>([]);
  const [edits, setEdits] = useState<Record<number, RowEdit>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  // CO-354 — per-company enablement of notification type 112 (AssetReplenishment).
  // 🔴 `rules` holds the WHOLE CompanyRulesView payload because UpdateCompanyRules assigns several
  // non-nullable members unconditionally; POSTing only the one flag would deserialize the rest to
  // their C# defaults and wipe the tenant's attendance configuration. Same round-trip contract as
  // Settings/Notifications/components/EngagementJobsTab.tsx. Only `notifyEnabled` is mutated locally.
  const [rules, setRules] = useState<CompanyRulesInput | null>(null);
  // 🔴 Default OFF. An undefined/absent value from the server is read as false, never as on.
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyBaseline, setNotifyBaseline] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await getReplenishmentSettings();
      if (result.errors.length === 0) {
        setRows(result.payload);
        const next: Record<number, RowEdit> = {};
        result.payload.forEach((row) => {
          next[row.categoryId] = toEdit(row);
        });
        setEdits(next);
      } else {
        setError(true);
      }

      // The notification toggle is a nice-to-have on this tab: a failure to read CompanyRules must
      // not blank the thresholds table, so it is loaded best-effort and simply stays OFF/hidden.
      if (companyId) {
        try {
          const rulesResult = await getCompanyRules(companyId);
          if (!rulesResult.errors?.length && rulesResult.payload) {
            const payload = rulesResult.payload;
            setRules(payload as unknown as CompanyRulesInput);
            const enabled = payload.enableAssetReplenishmentNotification === true;
            setNotifyEnabled(enabled);
            setNotifyBaseline(enabled);
          }
        } catch {
          /* toggle stays at its OFF default */
        }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirtyCategoryIds = useMemo(() => {
    return rows
      .filter((row) => {
        const edit = edits[row.categoryId];
        if (!edit) return false;
        const original = toEdit(row);
        return edit.threshold !== original.threshold || edit.thresholdType !== original.thresholdType;
      })
      .map((row) => row.categoryId);
  }, [rows, edits]);

  const notifyDirty = notifyEnabled !== notifyBaseline;
  const hasUnsavedChanges = dirtyCategoryIds.length > 0 || notifyDirty;

  const handleReset = () => {
    const next: Record<number, RowEdit> = {};
    rows.forEach((row) => {
      next[row.categoryId] = toEdit(row);
    });
    setEdits(next);
    setNotifyEnabled(notifyBaseline);
  };

  const handleThresholdChange = (categoryId: number, value: string) => {
    setEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], threshold: value },
    }));
  };

  const handleTypeChange = (categoryId: number, type: ReplenishThresholdType) => {
    setEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], thresholdType: type },
    }));
  };

  const handleSave = async () => {
    // Validate bounds before saving: no negatives, and percentages capped at 100.
    for (const categoryId of dirtyCategoryIds) {
      const edit = edits[categoryId];
      if (edit.threshold === "") continue;
      const num = Number(edit.threshold);
      if (Number.isNaN(num) || num < 0) {
        showToast(t("replenishment.invalidThreshold"), "error");
        return;
      }
      if (edit.thresholdType === ReplenishThresholdType.Percentage && num > 100) {
        showToast(t("replenishment.invalidPercentage"), "error");
        return;
      }
    }
    setSaving(true);
    try {
      // CO-354 — save the notification toggle first, whole-DTO round trip (see the `rules` comment).
      if (notifyDirty && canEditNotificationToggle && companyId && rules) {
        const input: CompanyRulesInput = {
          ...rules,
          enableAssetReplenishmentNotification: notifyEnabled,
        };
        const rulesResult = await updateCompanyRules(companyId, input);
        if (rulesResult.errors?.length) {
          showToast(rulesResult.errors[0]?.description || t("Something went wrong"), "error");
          setSaving(false);
          return;
        }
        setNotifyBaseline(notifyEnabled);
      }

      let failed = false;
      for (const categoryId of dirtyCategoryIds) {
        const edit = edits[categoryId];
        const result = await updateReplenishmentThreshold({
          categoryId,
          threshold: edit.threshold === "" ? undefined : Number(edit.threshold),
          thresholdType: edit.thresholdType,
        });
        if (result.errors.length !== 0) {
          failed = true;
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
          break;
        }
      }
      if (!failed) {
        showToast(
          dirtyCategoryIds.length === 0 ? t("replenishment.notifySaved") : t("replenishment.saved"),
          "success"
        );
      }
      await load();
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold text-foreground">{t("replenishment.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("replenishment.subtitle")}</p>
      </div>

      {/* CO-354 — per-company enablement of the replenishment notification (type 112).
          Gates only the NOTIFICATION; the thresholds below stay editable and keep their data
          either way. Defaults OFF for every company. */}
      {!loading && rules && (
        <div className="rounded-lg border border-border bg-card p-4" data-testid="replenishment-notify-toggle">
          <div className="flex items-start gap-4">
            <Switch
              id="replenishment-notify-switch"
              checked={notifyEnabled}
              onCheckedChange={setNotifyEnabled}
              disabled={saving || !canEditNotificationToggle}
            />
            <div className="flex flex-col gap-1">
              <label
                htmlFor="replenishment-notify-switch"
                className="text-sm font-semibold text-foreground cursor-pointer"
              >
                {t("replenishment.notifyLabel")}
              </label>
              <span className="text-sm text-muted-foreground">
                {t("replenishment.notifyExplain")}
              </span>
              {!canEditNotificationToggle && (
                <span className="text-xs text-muted-foreground" data-testid="replenishment-notify-readonly">
                  {t("replenishment.notifyHrOnly")}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="replenishment-error">
          <p className="text-sm text-destructive">{t("replenishment.loadError")}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12" data-testid="replenishment-empty">
          <RefreshCw className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("replenishment.noItems")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("replenishment.noItemsDescription")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("replenishment.colSku")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("replenishment.colAvailable")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("replenishment.colTotalOwned")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1">
                    {t("replenishment.colThreshold")}
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground/70 hover:text-foreground"
                            aria-label={t("replenishment.infoTitle")}
                            data-testid="replenishment-threshold-info"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            className="rounded-md bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md border border-border max-w-xs"
                            sideOffset={5}
                          >
                            <p className="font-medium">{t("replenishment.infoTitle")}</p>
                            <p className="mt-1">{t("replenishment.infoAmount")}</p>
                            <p className="mt-1">{t("replenishment.infoPercentage")}</p>
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {rows.map((row) => {
                const edit = edits[row.categoryId] ?? toEdit(row);
                const isDirty = dirtyCategoryIds.includes(row.categoryId);
                const thresholdNum = Number(edit.threshold);
                const hasValidThreshold = edit.threshold !== "" && !Number.isNaN(thresholdNum) && thresholdNum > 0;
                const triggerBelow =
                  edit.thresholdType === ReplenishThresholdType.Percentage
                    ? Math.ceil((thresholdNum / 100) * (row.replenishBaseline ?? row.totalOwned))
                    : thresholdNum;
                return (
                  <tr
                    key={row.categoryId}
                    className={`transition-colors ${isDirty ? "bg-amber-50/60" : "hover:bg-muted/30"}`}
                    data-testid={`replenishment-row-${row.categoryId}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {row.skuName}
                      {row.l2Name && (
                        <span className="block text-xs text-muted-foreground font-normal">{row.l2Name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{row.available}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{row.totalOwned}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <div className="inline-flex rounded-lg border border-border overflow-hidden shrink-0">
                            <button
                              type="button"
                              onClick={() => handleTypeChange(row.categoryId, ReplenishThresholdType.Amount)}
                              className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                                edit.thresholdType === ReplenishThresholdType.Amount
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-card text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`threshold-type-amount-${row.categoryId}`}
                            >
                              {t("replenishment.typeAmount")}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTypeChange(row.categoryId, ReplenishThresholdType.Percentage)}
                              className={`px-2.5 py-1.5 text-xs font-medium border-s border-border transition-colors ${
                                edit.thresholdType === ReplenishThresholdType.Percentage
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-card text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`threshold-type-percentage-${row.categoryId}`}
                            >
                              {t("replenishment.typePercentage")}
                            </button>
                          </div>
                          <input
                            type="number"
                            min="0"
                            max={edit.thresholdType === ReplenishThresholdType.Percentage ? 100 : undefined}
                            value={edit.threshold}
                            onChange={(e) => handleThresholdChange(row.categoryId, e.target.value)}
                            placeholder={t("replenishment.noThreshold")}
                            className="w-24 px-2 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            data-testid={`threshold-value-${row.categoryId}`}
                          />
                        </div>
                        {hasValidThreshold && (
                          <p className="text-xs text-muted-foreground" data-testid={`threshold-trigger-message-${row.categoryId}`}>
                            {t("replenishment.triggerMessage", { count: triggerBelow })}
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CO-354 — the bar must also appear when the only pending change is the notification toggle
          (a company can have zero configurable SKUs and still want the notification off/on). */}
      {!loading && !error && (rows.length > 0 || notifyDirty) && (
        <StickyActionBar
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={saving}
          onReset={handleReset}
          onSave={handleSave}
          t={t}
          saveLabel={dirtyCategoryIds.length > 0 ? t("replenishment.saveCount", { count: dirtyCategoryIds.length }) : t("replenishment.saveChanges")}
        />
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
